import { Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang, fmtMoney, fmtDate } from "@/i18n";

const Row = ({ label, value, className = "", testId }) => (
  <div className={`flex justify-between py-1.5 ${className}`}>
    <span className="font-body text-slate-600 dark:text-slate-300">{label}</span>
    <span className="font-mono" data-testid={testId}>{value}</span>
  </div>
);

export const PayslipDialog = ({ slip, onOpenChange }) => {
  const { t, lang } = useLang();
  if (!slip) return null;
  const period = new Date(slip.year, slip.month - 1, 1).toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", { month: "long", year: "numeric" });

  return (
    <Dialog open={!!slip} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="payslip-dialog">
        <div id="print-area" className="space-y-4 text-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("payslip")}</DialogTitle>
            <DialogDescription>{t(`kind_${slip.kind}`)} · {period}</DialogDescription>
          </DialogHeader>
          <div className="border-y border-slate-200 dark:border-slate-700 py-3">
            <div className="font-semibold text-base" data-testid="payslip-employee-name">{slip.employee_name}</div>
            <div className="text-xs text-slate-500">{t(slip.employment_type)} · {slip.weekly_hours}h/{t("weekly_hours").split(" ")[0].toLowerCase()} · {t("days_basis")}: {slip.days_basis}</div>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {slip.kind === "salary" && (slip.overtime_pay > 0 || slip.sick_deduction > 0 || slip.unpaid_deduction > 0) && (
              <>
                <Row label={t("base_gross")} value={fmtMoney(slip.base_gross)} testId="payslip-base-gross" />
                {slip.overtime_pay > 0 && <Row label={`${t("overtime_pay")} (${slip.overtime_hours}h)`} value={`+ ${fmtMoney(slip.overtime_pay)}`} className="text-emerald-700 dark:text-emerald-300" testId="payslip-overtime-pay" />}
                {slip.sick_deduction > 0 && <Row label={`${t("sick_deduction")} (${slip.sick_days} ${t("days").toLowerCase()})`} value={`− ${fmtMoney(slip.sick_deduction)}`} className="text-rose-700 dark:text-rose-300" testId="payslip-sick-deduction" />}
                {slip.unpaid_deduction > 0 && <Row label={`${t("unpaid_deduction")} (${slip.unpaid_days} ${t("days").toLowerCase()})`} value={`− ${fmtMoney(slip.unpaid_deduction)}`} className="text-rose-700 dark:text-rose-300" testId="payslip-unpaid-deduction" />}
              </>
            )}
            {slip.kind === "salary" && slip.leave_days > 0 && <Row label={t("abs_annual_leave")} value={`${slip.leave_days} ${t("days").toLowerCase()}`} className="text-xs text-slate-500" />}
            <Row label={t("gross")} value={fmtMoney(slip.gross)} testId="payslip-gross" />
            <Row label={`${t("efka_employee")} ${slip.efka_employee_rate}%`} value={`− ${fmtMoney(slip.efka_employee)}`} />
            <Row label={t("taxable")} value={fmtMoney(slip.taxable)} />
            <Row label={t("fmy")} value={`− ${fmtMoney(slip.tax)}`} />
            <Row label={t("net")} value={fmtMoney(slip.net)} className="text-lg font-semibold text-emerald-700 dark:text-emerald-300 border-t-2 border-slate-900 dark:border-slate-200 mt-1 pt-3" testId="payslip-net" />
          </div>
          <div className="bg-slate-50 dark:bg-slate-800/60 rounded p-3 text-xs">
            <Row label={`${t("efka_employer")} ${slip.efka_employer_rate}%`} value={fmtMoney(slip.efka_employer)} />
            <Row label={t("employer_cost")} value={fmtMoney(slip.employer_cost)} className="font-semibold" />
          </div>
          {slip.status === "paid" && <div className="text-xs text-emerald-700">{t("payroll_paid_on")}: {fmtDate(slip.paid_date, lang)}</div>}
        </div>
        <div className="flex justify-end no-print">
          <Button size="sm" onClick={() => window.print()} className="bg-blue-800 hover:bg-blue-700" data-testid="payslip-print-button"><Printer size={14} className="mr-1" />{t("print_pdf")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
